/**
 * Babel AST Validator - validates files before Sandpack renders.
 * Layout components get a functional replacement (with children).
 * App.js passes through as-is. Other broken files get stubs.
 */

import { parse } from '@babel/parser';

const BABEL_OPTIONS = {
  sourceType: 'module',
  plugins: ['jsx', 'classProperties', 'optionalChaining', 'nullishCoalescingOperator'],
  errorRecovery: true,
};

// NAMED-EXPORT-STUB-FIX: every fallback below used to replace a broken file
// with a DEFAULT-export-only stub. Confirmed live: `context/CartContext.js`
// failed to parse (a regex elsewhere had corrupted a ternary), fell into the
// generic branch below, and the stub it got had no `CartProvider` export at
// all — App.jsx's `import { CartProvider } from './context/CartContext'`
// then resolved to `undefined`, and `<CartProvider>` crashed the WHOLE app
// with "Element type is invalid ... got: undefined" instead of just losing
// that one file's real behavior. Whatever broke the original source, its
// named exports (context Providers, custom hooks) are still referenced by
// other files and must exist as SOMETHING — a harmless no-op is a contained
// failure; a missing export is an app-wide crash.
function extractNamedExportStubs(code, skipName) {
  const names = new Set();
  const patterns = [
    /export\s+(?:async\s+)?function\s+(\w+)/g,
    /export\s+const\s+(\w+)/g,
  ];
  for (const re of patterns) {
    let m;
    while ((m = re.exec(code)) !== null) {
      // COLLISION-GUARD: the fallback below always declares a default-export
      // function named `skipName` (derived from the filename). If the broken
      // source ALSO had a named export of that same identifier, emitting both
      // would be a duplicate top-level declaration — a fresh syntax error on
      // top of the one we're trying to recover from.
      if (m[1] !== skipName) names.add(m[1]);
    }
  }
  if (names.size === 0) return '';
  return [...names].map((n) => {
    // Provider-shaped exports take { children } and must render them so the
    // subtree they wrap still mounts; everything else (hooks, helpers) gets
    // a safe empty-object return so destructuring never throws.
    if (/Provider$/.test(n)) {
      return `export function ${n}({ children }) { return children; }`;
    }
    return `export function ${n}() { return {}; }`;
  }).join('\n');
}

export function validateFile(code, filePath) {
  try {
    const ast = parse(code, BABEL_OPTIONS);
    if (ast.errors && ast.errors.length > 0) {
      return { valid: false, error: ast.errors[0].message || 'Parse error', line: ast.errors[0].loc?.line || 0 };
    }
    return { valid: true };
  } catch (e) {
    return { valid: false, error: e.message || 'Parse error', line: e.loc?.line || 0 };
  }
}

export function validateAllFiles(files) {
  var validFiles = {};
  var errors = [];
  var hasErrors = false;

  Object.keys(files).forEach(function(path) {
    var code = files[path];
    if (!path.endsWith('.js')) {
      validFiles[path] = code;
      return;
    }

    var result = validateFile(code, path);
    if (result.valid) {
      validFiles[path] = code;
    } else {
      hasErrors = true;
      errors.push({ path: path, error: result.error, line: result.line });

      var name = path.split('/').pop().replace('.js', '').replace(/[^a-zA-Z]/g, '') || 'Component';
      var namedStubs = extractNamedExportStubs(code, name);
      var withNamedStubs = function(stub) { return namedStubs ? stub + '\n' + namedStubs + '\n' : stub; };

      // App.js - always pass through (Sandpack needs it)
      if (path === '/App.js' || path.indexOf('App') !== -1) {
        validFiles[path] = code;
      }
      // Layout/structural components - replace with WORKING layout that renders children
      else if (/^(Layout|AppLayout|MainLayout|DashboardLayout|RootLayout)$/i.test(name)) {
        validFiles[path] = withNamedStubs('import React from "react";\nimport { Link } from "react-router-dom";\nexport default function ' + name + '({ children }) {\n  return (\n    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column"}}>\n      <header style={{background:"#1e293b",color:"#fff",padding:"12px 24px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>\n        <span style={{fontWeight:"bold",fontSize:"18px"}}>App</span>\n        <nav style={{display:"flex",gap:"16px"}}><Link to="/" style={{color:"#e2e8f0",textDecoration:"none"}}>Home</Link></nav>\n      </header>\n      <main style={{flex:1,padding:"24px",background:"#f9fafb"}}>{children}</main>\n    </div>\n  );\n}\n');
      }
      // Navbar/Header/Footer - replace with working version
      else if (/^(Navbar|Header|Nav|TopBar)$/i.test(name)) {
        validFiles[path] = withNamedStubs('import React from "react";\nexport default function ' + name + '() {\n  return <nav style={{background:"#1e293b",color:"#fff",padding:"12px 24px",display:"flex",alignItems:"center",justifyContent:"space-between"}}><span style={{fontWeight:"bold"}}>App</span></nav>;\n}\n');
      }
      else if (/^(Footer|Sidebar)$/i.test(name)) {
        validFiles[path] = withNamedStubs('import React from "react";\nexport default function ' + name + '() {\n  return <footer style={{background:"#1e293b",color:"#94a3b8",padding:"24px",textAlign:"center"}}>Footer</footer>;\n}\n');
      }
      // All other broken files - stub with children support
      else {
        validFiles[path] = withNamedStubs('import React from "react";\nexport default function ' + name + '({ children }) {\n  return <div style={{padding:24}}>{children || "' + name + '"}</div>;\n}\n');
      }
    }
  });

  return { validFiles: validFiles, errors: errors, hasErrors: hasErrors };
}
